import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLeafletComponent } from './useLeaflet';
import 'leaflet/dist/leaflet.css';

/**
 * @typedef {object} Center
 * @property {number} lat
 * @property {number} lon
 */

// Child component to get the map instance.
// It *must* be rendered by its parent (Map.js) only when useMapFn is a valid hook function.
const MapInstanceEffect = ({ useMapFn, onMapInstance }) => {
  // This is now an unconditional call, assuming parent ensures useMapFn is valid.
  const map = useMapFn();

  useEffect(() => {
    if (map && typeof onMapInstance === 'function') {
      onMapInstance(map);
    }
  }, [map, onMapInstance]);

  return null;
};

MapInstanceEffect.propTypes = {
  useMapFn: PropTypes.func.isRequired, // Parent MUST ensure this is the useMap hook function
  onMapInstance: PropTypes.func,
};

/**
 * @function Map
 * @param {object} props
 * @param {React.CSSProperties} props.style
 * @param {React.ReactNode} props.children
 * @param {Center} props.center
 * @param {number} props.zoom
 * @param {(center: Center) => void} props.onClick
 * @param {function} props.onZoomend
 * @param {import('react-leaflet').MapContainerProps & { onMapInstance?: (map: any) => void }} props.mapProps
 * @param {() => void} [props.onLeafletComponentReady]
 * @return {JSX.Element | null}
 */
const Map = ({
  style = null,
  children = null,
  center: { lat, lon } = { lat: 0, lon: 0 },
  zoom = 10,
  onClick,
  onZoomend = null,
  mapProps = {},
  onLeafletComponentReady = () => {},
}) => {
  const LeafletMapContainer = useLeafletComponent('MapContainer');
  const ActualUseMapHook = useLeafletComponent('useMap'); // This gets the useMap hook *function*

  useEffect(() => {
    if (LeafletMapContainer && typeof onLeafletComponentReady === 'function') {
      onLeafletComponentReady();
    }
  }, [LeafletMapContainer, onLeafletComponentReady]);

  const styleWithDefaults = {
    height: '100%',
    ...style,
  };

  // Do not render anything if the MapContainer component itself isn't loaded yet.
  if (!LeafletMapContainer) {
    return null;
  }

  return (
    <LeafletMapContainer
      className="sl-quiver-map"
      {...mapProps}
      style={styleWithDefaults}
      center={{ lat, lng: lon }}
      zoom={zoom}
      onClick={
        typeof onClick === 'function'
          ? ({ latlng }) => onClick({ lat: latlng.lat, lon: latlng.lng })
          : undefined
      }
      onZoomend={onZoomend}
    >
      {children}
      {/* Render MapInstanceEffect only if ActualUseMapHook (the function) is loaded and onMapInstance callback exists */}
      {ActualUseMapHook && typeof ActualUseMapHook === 'function' && mapProps.onMapInstance && (
        <MapInstanceEffect useMapFn={ActualUseMapHook} onMapInstance={mapProps.onMapInstance} />
      )}
    </LeafletMapContainer>
  );
};

Map.propTypes = {
  mapProps: PropTypes.shape({
    placeholder: PropTypes.node,
    whenCreated: PropTypes.func,
    whenReady: PropTypes.func,
    onMapInstance: PropTypes.func,
  }),
  children: PropTypes.node,
  style: PropTypes.shape({}),
  center: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),
  zoom: PropTypes.number,
  onClick: PropTypes.func,
  onZoomend: PropTypes.func,
  onLeafletComponentReady: PropTypes.func,
};

export default Map;
